"use strict";
// packages/shared-schemas/src/leadTaxonomy.ts
//
// Lead taxonomy + routing rules + broker availability schemas.
//
// All three documents are subscriber-configurable — nothing about a
// particular tenant's category list, deal types, or rotation rules is
// hardcoded in the platform. Sensible defaults seed on first lead
// capture; admin edits via the Lead Capture admin pages.
//
// Plan: ~/.claude/plans/this-was-a-full-idempotent-cerf.md
Object.defineProperty(exports, "__esModule", { value: true });
exports.CrmWriteBackStatusSchema = exports.AssignmentHistoryEntrySchema = exports.RoutingDispositionSchema = exports.AssignmentReasonSchema = exports.BrokerAvailabilitySchema = exports.BrokerCapabilitySchema = exports.OutOfOfficeSchema = exports.WeeklyScheduleSchema = exports.LeadRoutingRulesSchema = exports.CascadeTierSchema = exports.DEFAULT_CASCADE_PRIORITY = exports.NotificationConfigSchema = exports.QuietHoursSchema = exports.AU_TIMEZONE_LABELS = exports.AuTimezoneSchema = exports.AU_TIMEZONE_VALUES = exports.GeoRuleSchema = exports.AuStateCodeSchema = exports.AU_STATE_CODES = exports.RotationRuleSchema = exports.RotationStrategySchema = exports.DEFAULT_LEAD_TAXONOMY = exports.LeadTaxonomySchema = exports.DealTypeSchema = exports.ServiceCategorySchema = exports.LeadReferrerSchema = exports.MANUAL_LEAD_SOURCE_LABELS = exports.MANUAL_LEAD_CHANNEL_VALUES = exports.ManualLeadSourceSchema = exports.MANUAL_LEAD_SOURCE_VALUES = exports.LeadSourceSchema = exports.LEAD_SOURCE_VALUES = void 0;
exports.isAuTimezone = isAuTimezone;
const zod_1 = require("zod");
const leadTerritories_1 = require("./leadTerritories");
const leadClassification_1 = require("./leadClassification");
// ────────────────────────────────────────────────────────────────────────
// Lead Source Channels
//
// Canonical taxonomy for a lead doc's `source` field: the single source of
// truth consumed by writeLead() and the manual-entry callable. Covers both
// the automated capture channels (website form + ad webhooks) and the
// manual-entry channels a human picks in the "Add lead" form.
//
// NOTE: the public webhook (leadIngest) validates against its own narrower
// subset. The manual-only channels (phone / monday / nodify / smokeball)
// are NOT accepted over the webhook, only via the createManualLead callable.
// ────────────────────────────────────────────────────────────────────────
exports.LEAD_SOURCE_VALUES = [
    'website_form',
    'google_ads',
    'meta_ads',
    'facebook_lead_ads',
    'manual',
    'csv_import',
    'referral',
    'other',
    // Manual-entry channels (createManualLead only): inbound phone calls and
    // leads hand-keyed out of other CRMs.
    'phone',
    'monday',
    'nodify',
    'smokeball',
    // HOW the person reached the brokerage, for leads keyed by hand. The set
    // above conflated two different dimensions: the channel an enquiry arrived
    // through, and the system it was copied out of. These are channels.
    //
    // Wording deliberately tracks MyCRM's own source vocabulary where it exists
    // ('Referral', 'Self Generated' are live values on Loan Market contacts), so
    // the two systems describe the same lead the same way.
    'email',
    'sms',
    'social_dm',
    'walk_in',
    'existing_client',
    'self_generated',
];
exports.LeadSourceSchema = zod_1.z.enum(exports.LEAD_SOURCE_VALUES);
// The subset an operator (super_admin / administrator / marketing) may pick
// when hand-keying a lead captured off-platform: an inbound call, or a lead
// managed in another CRM (Monday / Nodify / Smokeball). Used by the
// createManualLead callable's request schema.
exports.MANUAL_LEAD_SOURCE_VALUES = [
    // Channel: how the enquiry actually reached the brokerage.
    'phone',
    'email',
    'sms',
    'social_dm',
    'walk_in',
    'referral',
    'website_form',
    'existing_client',
    'self_generated',
    'other',
    // Provenance: hand-keyed out of another system. Kept distinct from channel
    // in the UI so "arrived by email" and "copied out of Smokeball" stop being
    // mutually exclusive answers to the same question.
    'manual',
    'monday',
    'nodify',
    'smokeball',
];
exports.ManualLeadSourceSchema = zod_1.z.enum(exports.MANUAL_LEAD_SOURCE_VALUES);
/**
 * Channel values that are NOT a system-of-origin. Drives the grouped picker in
 * the Add-lead form and keeps the split honest in one place rather than two.
 */
exports.MANUAL_LEAD_CHANNEL_VALUES = [
    'phone', 'email', 'sms', 'social_dm', 'walk_in',
    'referral', 'website_form', 'existing_client', 'self_generated', 'other',
];
/** Human labels for the manual picker. */
exports.MANUAL_LEAD_SOURCE_LABELS = {
    phone: 'Phone call',
    email: 'Email',
    sms: 'SMS',
    social_dm: 'Social DM',
    walk_in: 'Walk-in',
    referral: 'Referral',
    website_form: 'Website',
    existing_client: 'Existing client',
    self_generated: 'Self generated',
    other: 'Other',
    manual: 'Manual entry',
    monday: 'Monday.com',
    nodify: 'Nodify',
    smokeball: 'Smokeball',
};
/**
 * Referrer attached to a lead whose source is `referral`.
 *
 * `referrerName` is always free text so an operator can record a referrer the
 * brokerage has not set up in MyCRM ("Ray White Brisbane") without being
 * blocked. `mycrmReferrerId` is populated ONLY when the typed name was matched
 * against the tenant's own `/jsonapi/referrers` list, which is GET-only: we can
 * link to an existing referrer but can never create one. Unmatched names still
 * travel to MyCRM via `sourceAdditionalDetails`.
 */
exports.LeadReferrerSchema = zod_1.z.object({
    referrerName: zod_1.z.string().trim().min(1).max(200),
    referrerOrganisation: zod_1.z.string().trim().max(200).optional(),
    mycrmReferrerId: zod_1.z.string().trim().max(64).optional(),
});
// ────────────────────────────────────────────────────────────────────────
// Lead Taxonomy
//
// companies/{companyId}/lead_taxonomy/default
// ────────────────────────────────────────────────────────────────────────
// Stable, machine-safe identifiers. Display labels are separate.
const ID_PATTERN = /^[a-z][a-z0-9_]{0,40}$/;
exports.ServiceCategorySchema = zod_1.z.object({
    id: zod_1.z.string().regex(ID_PATTERN, 'must be lowercase alphanumeric + underscore'),
    label: zod_1.z.string().min(1).max(80),
    order: zod_1.z.number().int().min(0).max(999),
    active: zod_1.z.boolean().default(true),
});
exports.DealTypeSchema = zod_1.z.object({
    id: zod_1.z.string().regex(ID_PATTERN),
    label: zod_1.z.string().min(1).max(80),
    order: zod_1.z.number().int().min(0).max(999),
});
exports.LeadTaxonomySchema = zod_1.z.object({
    serviceCategories: zod_1.z.array(exports.ServiceCategorySchema).max(50),
    // Map from serviceCategory.id -> array of deal types under that category.
    // A category with empty array OR missing key has no deal-type sub-routing.
    dealTypes: zod_1.z.record(zod_1.z.string(), zod_1.z.array(exports.DealTypeSchema).max(50)).default({}),
    // Map from serviceCategory.id -> the four classification questions' options
    // (leadClassification.ts). Absent, or a category missing from the map, means
    // the defaults apply (mortgage category only); see resolveClassificationOptions.
    classification: leadClassification_1.LeadTaxonomyClassificationSchema.optional(),
    // Audit
    defaultsSeededAt: zod_1.z.unknown().optional(),
    updatedAt: zod_1.z.unknown().optional(),
    updatedBy: zod_1.z.string().nullable().optional(),
});
// Default seed used on first capture for any tenant that hasn't yet
// configured their taxonomy. Subscribers can replace wholesale via the
// admin page.
exports.DEFAULT_LEAD_TAXONOMY = {
    serviceCategories: [
        { id: 'finance', label: 'Mortgage', order: 1, active: true },
        { id: 'asset_finance', label: 'Asset Finance', order: 2, active: true },
        { id: 'property', label: 'Property', order: 3, active: true },
        { id: 'conveyancing', label: 'Conveyancing', order: 5, active: true },
        { id: 'legal', label: 'Legal', order: 4, active: true },
    ],
    dealTypes: {
        finance: [
            { id: 'fhb', label: 'First Home Buyer', order: 1 },
            { id: 'upgrader', label: 'Upgrader', order: 2 },
            { id: 'downsizer', label: 'Downsizer', order: 3 },
            { id: 'smsf', label: 'SMSF', order: 4 },
            { id: 'investment', label: 'Investment', order: 5 },
            { id: 'commercial', label: 'Commercial', order: 6 },
            // Legacy readers only: new leads carry it as classification.loanPurpose.
            { id: 'refinance', label: 'Refinance', order: 7 },
        ],
        asset_finance: [],
        property: [],
        legal: [],
        conveyancing: [],
    },
};
// ────────────────────────────────────────────────────────────────────────
// Routing Rules
//
// companies/{companyId}/lead_routing_rules/default
// ────────────────────────────────────────────────────────────────────────
exports.RotationStrategySchema = zod_1.z.enum([
    'round_robin',
    'least_loaded',
    'random',
    'first_available',
]);
exports.RotationRuleSchema = zod_1.z.object({
    id: zod_1.z.string().min(1).max(64),
    name: zod_1.z.string().min(1).max(120),
    // Match key — any subset can be specified. More-specific rules win
    // per cascadePriority. null fields don't constrain the match.
    match: zod_1.z.object({
        serviceCategory: zod_1.z.string().regex(ID_PATTERN).nullable().optional(),
        dealType: zod_1.z.string().regex(ID_PATTERN).nullable().optional(),
        officeId: zod_1.z.string().min(1).max(120).nullable().optional(),
        // Classification constraints (routing v2, leadRoutingMatchV2.ts). Read only
        // by matchLeadRotationV2; an empty array or null does not constrain. A rule
        // carrying these is matched by v1 on its other fields alone, so they only
        // take effect under featureFlags.routing_v2 = enforce.
        loanPurpose: zod_1.z.array(zod_1.z.string().regex(ID_PATTERN)).max(20).nullable().optional(),
        propertyUse: zod_1.z.array(zod_1.z.string().regex(ID_PATTERN)).max(20).nullable().optional(),
        borrowerSituation: zod_1.z.array(zod_1.z.string().regex(ID_PATTERN)).max(20).nullable().optional(),
        loanStructure: zod_1.z.array(zod_1.z.string().regex(ID_PATTERN)).max(20).nullable().optional(),
        // Loan size bands (tenant-defined options of the loanAmount dimension).
        loanAmount: zod_1.z.array(zod_1.z.string().regex(ID_PATTERN)).max(20).nullable().optional(),
    }),
    strategy: exports.RotationStrategySchema,
    brokerIds: zod_1.z.array(zod_1.z.string().min(1).max(120)).min(1).max(50),
    // Optional per-rule overrides. null = inherit global default.
    slaOverrideMinutes: zod_1.z.number().int().min(1).max(10080).nullable().optional(),
    passOffOverrideMinutes: zod_1.z.number().int().min(1).max(10080).nullable().optional(),
});
// AU state codes — used for postcode + IP-geolocation routing.
exports.AU_STATE_CODES = ['VIC', 'NSW', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'];
exports.AuStateCodeSchema = zod_1.z.enum(exports.AU_STATE_CODES);
exports.GeoRuleSchema = zod_1.z.object({
    id: zod_1.z.string().min(1).max(64),
    states: zod_1.z.array(exports.AuStateCodeSchema).min(1).max(8),
    // Either route to a specific office (will then filter rotations by
    // that office), or directly to a rotation rule by ID.
    officeId: zod_1.z.string().min(1).max(120).nullable().optional(),
    rotationRuleId: zod_1.z.string().min(1).max(64).nullable().optional(),
});
// ────────────────────────────────────────────────────────────────────────
// Australian timezones
//
// The allow-list every saved routing timezone is validated against. Both the
// tenant quiet-hours zone and a broker's own zone were display-only before
// and always persisted as Australia/Sydney, so a Brisbane broker was judged
// one hour wrong and a Perth broker three. Routing is an Australian-only
// product, so the set is closed rather than free text: a typo in an IANA
// name would otherwise make Intl fall back and misjudge hours silently.
// ────────────────────────────────────────────────────────────────────────
exports.AU_TIMEZONE_VALUES = [
    'Australia/Sydney',
    'Australia/Melbourne',
    'Australia/Brisbane',
    'Australia/Adelaide',
    'Australia/Perth',
    'Australia/Hobart',
    'Australia/Darwin',
    'Australia/Lord_Howe',
];
exports.AuTimezoneSchema = zod_1.z.enum(exports.AU_TIMEZONE_VALUES);
/** Labels for the timezone pickers. Plain place names plus the UTC offset
 *  family, so an admin does not have to know which zones observe DST. */
exports.AU_TIMEZONE_LABELS = {
    'Australia/Sydney': 'Sydney (NSW)',
    'Australia/Melbourne': 'Melbourne (VIC)',
    'Australia/Brisbane': 'Brisbane (QLD, no daylight saving)',
    'Australia/Adelaide': 'Adelaide (SA)',
    'Australia/Perth': 'Perth (WA, no daylight saving)',
    'Australia/Hobart': 'Hobart (TAS)',
    'Australia/Darwin': 'Darwin (NT, no daylight saving)',
    'Australia/Lord_Howe': 'Lord Howe Island',
};
/** True when `value` is a timezone this platform will route on. */
function isAuTimezone(value) {
    return typeof value === 'string' && exports.AU_TIMEZONE_VALUES.includes(value);
}
exports.QuietHoursSchema = zod_1.z.object({
    enabled: zod_1.z.boolean().default(true),
    // 24-hour HH:MM
    start: zod_1.z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).default('22:00'),
    end: zod_1.z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).default('07:00'),
    timezone: exports.AuTimezoneSchema.default('Australia/Sydney'),
    mode: zod_1.z.enum(['queue', 'on_call', 'assign_anyway']).default('queue'),
    onCallBrokerIds: zod_1.z.array(zod_1.z.string().min(1).max(120)).max(50).default([]),
});
exports.NotificationConfigSchema = zod_1.z.object({
    email: zod_1.z.boolean().default(true),
    inApp: zod_1.z.boolean().default(true),
    // SMS reserved for future paid-add-on. Always false for V1; persisted
    // so the admin UI can show the option as "coming soon" without a
    // schema migration later.
    sms: zod_1.z.boolean().default(false),
});
// The routing cascade tiers, most specific first. THE single definition:
// matchLeadRotation (v1), matchLeadRotationV2, pickAssignee and the routing
// UI all import it rather than keeping their own copy.
exports.DEFAULT_CASCADE_PRIORITY = [
    'service+dealType+office',
    'service+dealType',
    'service+office',
    'service',
    'office',
    'company',
];
exports.CascadeTierSchema = zod_1.z.enum(exports.DEFAULT_CASCADE_PRIORITY);
exports.LeadRoutingRulesSchema = zod_1.z.object({
    rotations: zod_1.z.array(exports.RotationRuleSchema).max(100).default([]),
    // Cascade order — most-specific match wins. Default below covers the
    // four common axes; admin shouldn't normally change this.
    cascadePriority: zod_1.z.array(exports.CascadeTierSchema).default([...exports.DEFAULT_CASCADE_PRIORITY]),
    // Globals — every value below is configurable per subscriber.
    defaultSlaMinutes: zod_1.z.number().int().min(1).max(10080).default(15),
    defaultPassOffMinutes: zod_1.z.number().int().min(1).max(10080).default(480),
    defaultMaxOpenLeads: zod_1.z.number().int().min(1).max(500).default(25),
    defaultStrategy: exports.RotationStrategySchema.default('round_robin'),
    defaultMaxReassignments: zod_1.z.number().int().min(1).max(20).default(3),
    defaultFallback: zod_1.z.enum([
        'office_admin_queue',
        'random_assign',
        'queue_until_open',
    ]).default('office_admin_queue'),
    // Who owns a lead routing would otherwise leave unowned: no rotation
    // matched, or the pool stayed empty after every retry. The tenant picks
    // these people on the routing page, in priority order; the first active
    // member not on leave gets the lead. Temporary holds (quiet hours, the
    // pool retry) are untouched: they resolve on their own. Empty or absent =
    // the lead stays unassigned for triage, as before. Optional with no
    // default so a save from an older client cannot wipe the stored list.
    fallbackOwnerUids: zod_1.z.array(zod_1.z.string().min(1).max(128).regex(/^[^/]+$/)).max(10).optional(),
    // When nobody in the matched pool is eligible (all on leave, paused, outside
    // their hours or at capacity) the lead used to be dropped unassigned with
    // queuedUntil: null, and nothing ever looked at it again. Re-queue it this
    // many minutes later so processQueuedLeads retries once the roster changes.
    // 0 disables retrying and restores the old drop-on-the-floor behaviour.
    retryWhenNoneEligibleMinutes: zod_1.z.number().int().min(0).max(1440).default(15),
    // Bound the retry loop so a permanently empty pool cannot re-queue a lead
    // forever. After this many retries the lead is left unassigned with a
    // visible reason for an admin to pick up.
    maxNoneEligibleRetries: zod_1.z.number().int().min(1).max(100).default(8),
    quietHours: exports.QuietHoursSchema.default(exports.QuietHoursSchema.parse({})),
    geoRules: zod_1.z.array(exports.GeoRuleSchema).max(50).default([]),
    // EXPLICIT on/off for postcode territory routing. This is the setting the
    // router reads; the presence or shape of `postcodeTerritories` must never
    // decide it on its own (an empty list saved by the config page used to flip
    // every tenant into territory mode, where an uncovered postcode returns
    // unassigned immediately).
    //   true      -> territory routing, whatever the list looks like.
    //   false     -> legacy rotation; any stored territories are kept as a draft.
    //   undefined -> backward compatibility only: territory routing iff a
    //                non-empty list was already saved before this flag existed.
    territoryRoutingEnabled: zod_1.z.boolean().optional(),
    // Optional so older clients cannot erase territories by omitting new fields.
    // Uncovered leads go to HQ triage when territory routing is on.
    postcodeTerritories: leadTerritories_1.PostcodeTerritoriesSchema.optional(),
    territoryFallbackOfficeId: zod_1.z.string().min(1).max(120).regex(/^[^/]+$/).nullable().optional(),
    geoFallback: zod_1.z.enum(['company_default', 'office_round_robin']).default('company_default'),
    notifyOnAssignment: exports.NotificationConfigSchema.default(exports.NotificationConfigSchema.parse({})),
    // State — server-managed cursor advance per rotation rule.
    // Keyed by rotation rule ID. Index into rule.brokerIds[] for the
    // next round-robin assignment.
    rotationCursors: zod_1.z.record(zod_1.z.string(), zod_1.z.number().int().min(0)).default({}),
    updatedAt: zod_1.z.unknown().optional(),
    updatedBy: zod_1.z.string().nullable().optional(),
});
// ────────────────────────────────────────────────────────────────────────
// Broker Availability
//
// companies/{companyId}/brokers/{brokerId}/availability/default
// ────────────────────────────────────────────────────────────────────────
const DayWindowSchema = zod_1.z.object({
    start: zod_1.z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    end: zod_1.z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
});
exports.WeeklyScheduleSchema = zod_1.z.object({
    mon: zod_1.z.array(DayWindowSchema).max(4).default([]),
    tue: zod_1.z.array(DayWindowSchema).max(4).default([]),
    wed: zod_1.z.array(DayWindowSchema).max(4).default([]),
    thu: zod_1.z.array(DayWindowSchema).max(4).default([]),
    fri: zod_1.z.array(DayWindowSchema).max(4).default([]),
    sat: zod_1.z.array(DayWindowSchema).max(4).default([]),
    sun: zod_1.z.array(DayWindowSchema).max(4).default([]),
});
exports.OutOfOfficeSchema = zod_1.z.object({
    enabled: zod_1.z.boolean().default(false),
    until: zod_1.z.unknown().nullable().optional(), // Timestamp at runtime
    reason: zod_1.z.string().max(200).nullable().optional(),
});
exports.BrokerCapabilitySchema = zod_1.z.object({
    serviceCategory: zod_1.z.string().regex(ID_PATTERN),
    dealTypes: zod_1.z.array(zod_1.z.string().regex(ID_PATTERN)).default([]),
});
exports.BrokerAvailabilitySchema = zod_1.z.object({
    weeklySchedule: exports.WeeklyScheduleSchema.default(exports.WeeklyScheduleSchema.parse({})),
    // The zone the weekly schedule and the out-of-office end date are judged in.
    // Editable on the My Availability page; a broker with no saved doc inherits
    // the tenant's quiet-hours zone at routing time, NOT a hardcoded Sydney.
    timezone: exports.AuTimezoneSchema.default('Australia/Sydney'),
    outOfOffice: exports.OutOfOfficeSchema.default(exports.OutOfOfficeSchema.parse({})),
    // Per-broker cap. null = inherit company default.
    maxOpenLeads: zod_1.z.number().int().min(1).max(500).nullable().optional(),
    acceptingNewLeads: zod_1.z.boolean().default(true),
    // Capabilities are mostly informational — actual routing eligibility is
    // determined by which rotations include the broker's ID. Capabilities
    // serve as documentation + admin search/filter when building rules.
    capabilities: zod_1.z.array(exports.BrokerCapabilitySchema).max(50).default([]),
    updatedAt: zod_1.z.unknown().optional(),
    updatedBy: zod_1.z.string().nullable().optional(),
});
// ────────────────────────────────────────────────────────────────────────
// Lead document extensions (additive — overlay on existing lead schema)
// ────────────────────────────────────────────────────────────────────────
exports.AssignmentReasonSchema = zod_1.z.enum([
    'initial_round_robin',
    'initial_least_loaded',
    'initial_random',
    'initial_first_available',
    'sla_escalation',
    'manual_admin',
    'broker_decline',
    'queued_to_business_hours',
    'no_rotation_match',
    'pool_exhausted',
    // Nobody was eligible, but the lead has been re-queued for another attempt
    // rather than dropped. Distinct from 'pool_exhausted' so an admin can tell
    // "still trying" from "given up on".
    'pool_exhausted_retry',
    // No rotation could take the lead, so it went to the first available
    // person on the tenant's fallback list (fallbackOwnerUids).
    'fallback_owner',
]);
// Lifecycle disposition for a lead's routing. `'auto'` (the implicit
// default when the field is absent/null) means the schedulers may continue
// to act on the lead — escalate it, drain it from the quiet-hours queue.
// `'manual_triage'` is the terminal off-ramp: a human has taken ownership
// of the routing decision, so BOTH the SLA escalator and the quiet-hours
// sweeper skip the lead entirely and leave it for admin to resolve.
exports.RoutingDispositionSchema = zod_1.z.enum(['auto', 'manual_triage']);
exports.AssignmentHistoryEntrySchema = zod_1.z.object({
    brokerId: zod_1.z.string().nullable(),
    assignedAt: zod_1.z.unknown(), // Timestamp
    reason: exports.AssignmentReasonSchema,
    cursorPosition: zod_1.z.number().int().nullable().optional(),
    rotationRuleId: zod_1.z.string().nullable().optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// CRM write-back fields (Phase C2 scaffolding — pending LMG /jsonapi/leads spec)
//
// Additive — overlaid on the lead doc after initial capture.
// TODO: finalise field names + semantics once LMG spec lands (Phase D1).
// No Firestore index changes — crmWriteBackStatus will be indexed in a
// follow-up PR once the query pattern is confirmed (Phase D2 or later).
// ─────────────────────────────────────────────────────────────────────────────
/** Tri-state write-back lifecycle for a single CRM connection per lead. */
exports.CrmWriteBackStatusSchema = zod_1.z.enum([
    'pending',
    'sent',
    'failed',
    'skipped',
    'observed',
]);
